import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UnitProvider, useUnit } from './contexts/UnitContext';
import { Toaster } from './components/ui/sonner';
import AiAssistant from './components/AiAssistant';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Associates from './pages/Associates';
import EmployeeProfile from './pages/EmployeeProfile';
import ExEmployees from './pages/ExEmployees';
import OrgChart from './pages/OrgChart';
import Backup from './pages/Backup';
import RBACAuth from './pages/RBACAudit';
import SalarySheet from './pages/SalarySheet';
import UnitPicker from './pages/UnitPicker';
import Settings from './pages/Settings';
import DeptChart from './pages/DeptChart';
import KpiPortal from './pages/KpiPortal';
import LateComing from './pages/LateComing';
import Expense from './pages/Expense';
import Accident from './pages/Accident';
import Attrition from './pages/Attrition';
import TrainingPlan from './pages/TrainingPlan';
import RequirementTat from './pages/RequirementTat';
import KpiCenter from './pages/KpiCenter';
import PreJoining from './pages/PreJoining';
import RecycleBin from './pages/RecycleBin';
import LetterHistory from './pages/LetterHistory';
import TransferLetter from './pages/TransferLetter';
import CorporateHQ from './pages/CorporateHQ';
import KpiQrSheet from './pages/KpiQrSheet';
import Suggestions from './pages/Suggestions';
import Kaizen from './pages/Kaizen';
import EmailCenter from './pages/EmailCenter';
import RbacSettings from './pages/RbacSettings';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div data-testid="loading-screen" className="flex items-center justify-center min-h-screen bg-[#F7F8FB]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-[#5A6377]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function UnitGate({ children }) {
  const { activeUnit } = useUnit();
  if (!activeUnit) return <Navigate to="/select-unit" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Hidden on the login screen and on the public KPI portal, which employees
  // reach by QR without an HR session.
  const showAssistant = !!user && pathname !== '/login' && !pathname.startsWith('/kpi/');
  // The confirmation prompt links into an employee profile, and those routes
  // sit behind the unit gate — showing it on the unit picker gives HR a button
  // that bounces straight back here.

  return (
    <>
    <Routes>
      <Route path="/login" element={user && user !== false ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/kpi/:token" element={<KpiPortal />} />
      <Route path="/select-unit" element={<ProtectedRoute><UnitPicker /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Dashboard />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/corporate-hq"
        element={
          <ProtectedRoute>
            <UnitGate>
              <CorporateHQ />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      {/* The shop floor. Behind the unit gate like Employees: an associate
          belongs to one plant and the register is read per plant. */}
      <Route
        path="/associates"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Associates />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpi-qr-sheet"
        element={
          <ProtectedRoute>
            <UnitGate>
              <KpiQrSheet />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email"
        element={
          <ProtectedRoute>
            <UnitGate>
              <EmailCenter />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/suggestions"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Suggestions />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/late-coming"
        element={
          <ProtectedRoute>
            <UnitGate>
              <LateComing />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expense"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Expense />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accident"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Accident />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/attrition"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Attrition />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training-plan"
        element={
          <ProtectedRoute>
            <UnitGate>
              <TrainingPlan />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      {/* /recruitment-tat, /customer-score and /customer-complaint-card are
          gone. All three were hand-typed registers that stayed empty.
          Recruitment TAT is now derived from vacancies + joining dates;
          the two customer measures move to another system and will arrive
          over the API bridge. The page files are kept in the repo so the
          screens can be restored if any of that changes. */}
      <Route
        path="/requirement-tat"
        element={
          <ProtectedRoute>
            <UnitGate>
              <RequirementTat />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kaizen"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Kaizen />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
            <UnitGate>
              <Employees />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <ProtectedRoute>
            <UnitGate>
              <EmployeeProfile />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ex-employees"
        element={
          <ProtectedRoute>
            <UnitGate>
              <ExEmployees />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/salary-sheet"
        element={
          <ProtectedRoute>
            <UnitGate>
              <SalarySheet />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orgchart"
        element={
          <ProtectedRoute>
            <UnitGate>
              <OrgChart />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dept-chart"
        element={
          <ProtectedRoute>
            <UnitGate>
              <DeptChart />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      {/* Vacancies and Requirement TAT are one screen now — an open row there
          IS a vacancy. Old links and bookmarks land on it. */}
      <Route
        path="/vacancies"
        element={<Navigate to="/requirement-tat" replace />}
      />
      <Route
        path="/backup"
        element={
          <ProtectedRoute>
            <Backup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rbac-audit"
        element={
          <ProtectedRoute>
            <RBACAuth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rbac-settings"
        element={
          <ProtectedRoute>
            <UnitGate>
              <RbacSettings />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recycle-bin"
        element={
          <ProtectedRoute>
            <RecycleBin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/letters"
        element={
          <ProtectedRoute>
            <LetterHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-letter"
        element={
          <ProtectedRoute>
            <TransferLetter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpi-center"
        element={
          <ProtectedRoute>
            <UnitGate>
              <KpiCenter />
            </UnitGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pre-joining"
        element={
          <ProtectedRoute>
            <UnitGate>
              <PreJoining />
            </UnitGate>
          </ProtectedRoute>
        }
      />
    </Routes>
    {showAssistant && <AiAssistant />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <UnitProvider>
          <AppRoutes />
        </UnitProvider>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
